'use client';

/**
 * DateRangeSlider — relative date-axis dual-thumb range.
 *
 * The thumbs move along a 0–100% axis where 0% = `spanDays` ago and 100% =
 * today (the reference "now"). Values are PERCENT positions so the caller can
 * persist them URL-cleanly without timezone math; {@link percentToDate}
 * resolves a percent to an absolute `Date` against `now`. Labels follow the
 * mockup's `after / before / range / Any` contract — shared with the
 * SortFilterBar chip summary via {@link formatDateRangeValueLabel}.
 */
import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useRef, useState } from 'react';
import { type DateRangeLabels, EPSILON, formatDateEndpoint, formatDateRangeValueLabel } from '@/components/ui/date-range-slider-format';
import { cn } from '@/lib/ui/utils';
const STEP_PCT = 0.5;
function clampPercent(v: number): number {
  return Math.max(0, Math.min(100, v));
}
export interface DateRangeSliderProps {
  label: string;
  /** Number of days the axis spans, ending at `now`. */
  spanDays: number;
  /** Controlled `[lowPercent, highPercent]` (0–100, 0 = oldest). */
  value: [number, number];
  onChange: (next: [number, number]) => void;
  /** Reference "now" epoch ms. Defaults to `Date.now()`. */
  now?: number | undefined;
  /** i18n overrides for the `any/after/before` vocabulary. */
  labels?: DateRangeLabels | undefined;
  className?: string | undefined;
}
export function DateRangeSlider(t0) {
  const $ = _c(109);
  const {
    label,
    spanDays,
    value,
    onChange,
    now,
    labels,
    className
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  const trackRef = useRef(null);
  const [mountNow] = useState(_temp);
  const referenceNow = now ?? mountNow;
  const [loPct, hiPct] = value;
  let t1;
  if ($[0] !== hiPct || $[1] !== labels || $[2] !== loPct || $[3] !== referenceNow || $[4] !== spanDays) {
    t1 = formatDateRangeValueLabel(loPct, hiPct, spanDays, referenceNow, labels);
    $[0] = hiPct;
    $[1] = labels;
    $[2] = loPct;
    $[3] = referenceNow;
    $[4] = spanDays;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const valueLabel = t1;
  let t2;
  if ($[6] !== referenceNow || $[7] !== spanDays) {
    t2 = formatDateEndpoint(0, spanDays, referenceNow);
    $[6] = referenceNow;
    $[7] = spanDays;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== referenceNow || $[10] !== spanDays) {
    t3 = formatDateEndpoint(100, spanDays, referenceNow);
    $[9] = referenceNow;
    $[10] = spanDays;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const endsLabel = `${t2} — ${t3}`;
  const isActive = loPct > EPSILON || hiPct < 100 - EPSILON;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[12] !== className || $[13] !== endsLabel || $[14] !== hiPct || $[15] !== isActive || $[16] !== label || $[17] !== loPct || $[18] !== onChange || $[19] !== referenceNow || $[20] !== shouldReduceMotion || $[21] !== spanDays || $[22] !== valueLabel) {
    const percentFromClientX = function percentFromClientX(clientX) {
      const rect = trackRef.current?.getBoundingClientRect();
      if (rect === undefined || rect.width === 0) {
        return 0;
      }
      return clampPercent((clientX - rect.left) / rect.width * 100);
    };
    const handleDrag = which => event => {
      event.preventDefault();
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
      const move = e => {
        const p = percentFromClientX(e.clientX);
        if (which === "lo") {
          onChange([clampPercent(Math.min(p, hiPct - STEP_PCT)), hiPct]);
        } else {
          onChange([loPct, clampPercent(Math.max(p, loPct + STEP_PCT))]);
        }
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    };
    let t23;
    if ($[42] !== hiPct || $[43] !== loPct || $[44] !== onChange) {
      t23 = which_0 => event_0 => {
        const stepPct = event_0.shiftKey ? STEP_PCT * 20 : STEP_PCT * 2;
        let delta;
        bb42: switch (event_0.key) {
          case "ArrowRight":
          case "ArrowUp":
            {
              delta = stepPct;
              break bb42;
            }
          case "ArrowLeft":
          case "ArrowDown":
            {
              delta = -stepPct;
              break bb42;
            }
          case "Home":
            {
              delta = which_0 === "lo" ? -loPct : -hiPct;
              break bb42;
            }
          case "End":
            {
              delta = which_0 === "lo" ? 100 - loPct : 100 - hiPct;
              break bb42;
            }
          default:
            {
              return;
            }
        }
        event_0.preventDefault();
        if (which_0 === "lo") {
          onChange([clampPercent(Math.min(loPct + delta, hiPct - STEP_PCT)), hiPct]);
        } else {
          onChange([loPct, clampPercent(Math.max(hiPct + delta, loPct + STEP_PCT))]);
        }
      };
      $[42] = hiPct;
      $[43] = loPct;
      $[44] = onChange;
      $[45] = t23;
    } else {
      t23 = $[45];
    }
    const handleKeyDown = t23;
    t18 = "date-range-slider";
    t19 = isActive;
    if ($[46] !== className) {
      t20 = cn("[--range-slider-track:var(--background-tertiary)]", "[--range-slider-fill:var(--button-bg)]", "[--range-slider-thumb-bg:var(--background-primary)]", "[--range-slider-thumb-border:var(--button-bg)]", "select-none px-0.5 pt-0.5", className);
      $[46] = className;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    const t24 = isActive ? "text-foreground-primary" : "text-foreground-secondary";
    let t25;
    if ($[48] !== t24) {
      t25 = cn("text-caption font-medium tabular-nums", t24);
      $[48] = t24;
      $[49] = t25;
    } else {
      t25 = $[49];
    }
    let t26;
    if ($[50] !== t25 || $[51] !== valueLabel) {
      t26 = <span className={t25}>{valueLabel}</span>;
      $[50] = t25;
      $[51] = valueLabel;
      $[52] = t26;
    } else {
      t26 = $[52];
    }
    let t27;
    if ($[53] !== endsLabel) {
      t27 = <span className="text-micro tabular-nums text-foreground-tertiary">{endsLabel}</span>;
      $[53] = endsLabel;
      $[54] = t27;
    } else {
      t27 = $[54];
    }
    if ($[55] !== t26 || $[56] !== t27) {
      t21 = <div className="mb-0.5 flex items-baseline justify-between gap-2">{t26}{t27}</div>;
      $[55] = t26;
      $[56] = t27;
      $[57] = t21;
    } else {
      t21 = $[57];
    }
    t13 = trackRef;
    t14 = "date-range-slider-track";
    t15 = "relative mx-3 mt-4 mb-2 h-1.5 touch-none rounded-full bg-(--range-slider-track)";
    const t28 = `${loPct}%`;
    const t29 = `${Math.max(0, hiPct - loPct)}%`;
    if ($[58] !== t28 || $[59] !== t29) {
      t16 = <div aria-hidden="true" className="absolute inset-y-0 rounded-full bg-(--range-slider-fill)" style={{
        left: t28,
        width: t29
      }} />;
      $[58] = t28;
      $[59] = t29;
      $[60] = t16;
    } else {
      t16 = $[60];
    }
    let t30;
    if ($[61] !== loPct) {
      t30 = Math.round(loPct);
      $[61] = loPct;
      $[62] = t30;
    } else {
      t30 = $[62];
    }
    let t31;
    if ($[63] !== loPct || $[64] !== referenceNow || $[65] !== spanDays) {
      t31 = formatDateEndpoint(loPct, spanDays, referenceNow);
      $[63] = loPct;
      $[64] = referenceNow;
      $[65] = spanDays;
      $[66] = t31;
    } else {
      t31 = $[66];
    }
    const t32 = handleDrag("lo");
    let t33;
    if ($[67] !== handleKeyDown) {
      t33 = handleKeyDown("lo");
      $[67] = handleKeyDown;
      $[68] = t33;
    } else {
      t33 = $[68];
    }
    const t34 = shouldReduceMotion === true ? undefined : "transition-transform active:scale-110";
    let t35;
    if ($[69] !== t34) {
      t35 = cn("absolute top-1/2 size-[18px] -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full", "border-2 border-(--range-slider-thumb-border) bg-(--range-slider-thumb-bg) shadow-elevation-card outline-none", "focus-visible:ring-2 focus-visible:ring-border-primary/50", t34);
      $[69] = t34;
      $[70] = t35;
    } else {
      t35 = $[70];
    }
    const t36 = `${loPct}%`;
    let t37;
    if ($[71] !== t36) {
      t37 = {
        left: t36
      };
      $[71] = t36;
      $[72] = t37;
    } else {
      t37 = $[72];
    }
    t17 = <div role="slider" tabIndex={0} aria-label={`${label} after`} aria-orientation="horizontal" aria-valuemin={0} aria-valuemax={100} aria-valuenow={t30} aria-valuetext={t31} onPointerDown={t32} onKeyDown={t33} className={t35} style={t37} />;
    t4 = "slider";
    t5 = 0;
    t6 = `${label} before`;
    t7 = "horizontal";
    t8 = 0;
    t9 = 100;
    if ($[73] !== hiPct) {
      t10 = Math.round(hiPct);
      $[73] = hiPct;
      $[74] = t10;
    } else {
      t10 = $[74];
    }
    if ($[75] !== hiPct || $[76] !== referenceNow || $[77] !== spanDays) {
      t11 = formatDateEndpoint(hiPct, spanDays, referenceNow);
      $[75] = hiPct;
      $[76] = referenceNow;
      $[77] = spanDays;
      $[78] = t11;
    } else {
      t11 = $[78];
    }
    t12 = handleDrag("hi");
    t22 = handleKeyDown("hi");
    $[12] = className;
    $[13] = endsLabel;
    $[14] = hiPct;
    $[15] = isActive;
    $[16] = label;
    $[17] = loPct;
    $[18] = onChange;
    $[19] = referenceNow;
    $[20] = shouldReduceMotion;
    $[21] = spanDays;
    $[22] = valueLabel;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
    $[34] = t21;
    $[35] = t22;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
  } else {
    t10 = $[23];
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
    t15 = $[28];
    t16 = $[29];
    t17 = $[30];
    t18 = $[31];
    t19 = $[32];
    t20 = $[33];
    t21 = $[34];
    t22 = $[35];
    t4 = $[36];
    t5 = $[37];
    t6 = $[38];
    t7 = $[39];
    t8 = $[40];
    t9 = $[41];
  }
  const t23 = shouldReduceMotion === true ? undefined : "transition-transform active:scale-110";
  let t24;
  if ($[79] !== t23) {
    t24 = cn("absolute top-1/2 size-[18px] -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full", "border-2 border-(--range-slider-thumb-border) bg-(--range-slider-thumb-bg) shadow-elevation-card outline-none", "focus-visible:ring-2 focus-visible:ring-border-primary/50", t23);
    $[79] = t23;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  const t25 = `${hiPct}%`;
  let t26;
  if ($[81] !== t25) {
    t26 = {
      left: t25
    };
    $[81] = t25;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] !== t10 || $[84] !== t11 || $[85] !== t12 || $[86] !== t22 || $[87] !== t24 || $[88] !== t26 || $[89] !== t4 || $[90] !== t5 || $[91] !== t6 || $[92] !== t7 || $[93] !== t8 || $[94] !== t9) {
    t27 = <div role={t4} tabIndex={t5} aria-label={t6} aria-orientation={t7} aria-valuemin={t8} aria-valuemax={t9} aria-valuenow={t10} aria-valuetext={t11} onPointerDown={t12} onKeyDown={t22} className={t24} style={t26} />;
    $[83] = t10;
    $[84] = t11;
    $[85] = t12;
    $[86] = t22;
    $[87] = t24;
    $[88] = t26;
    $[89] = t4;
    $[90] = t5;
    $[91] = t6;
    $[92] = t7;
    $[93] = t8;
    $[94] = t9;
    $[95] = t27;
  } else {
    t27 = $[95];
  }
  let t28;
  if ($[96] !== t13 || $[97] !== t14 || $[98] !== t15 || $[99] !== t16 || $[100] !== t17 || $[101] !== t27) {
    t28 = <div ref={t13} data-slot={t14} className={t15}>{t16}{t17}{t27}</div>;
    $[96] = t13;
    $[97] = t14;
    $[98] = t15;
    $[99] = t16;
    $[100] = t17;
    $[101] = t27;
    $[102] = t28;
  } else {
    t28 = $[102];
  }
  let t29;
  if ($[103] !== t18 || $[104] !== t19 || $[105] !== t20 || $[106] !== t21 || $[107] !== t28) {
    t29 = <div data-slot={t18} data-active={t19} className={t20}>{t21}{t28}</div>;
    $[103] = t18;
    $[104] = t19;
    $[105] = t20;
    $[106] = t21;
    $[107] = t28;
    $[108] = t29;
  } else {
    t29 = $[108];
  }
  return t29;
}
function _temp() {
  return Date.now();
}